import React, { useEffect, useState } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Box, Typography, Tooltip } from '@mui/material';
import axiosInstance from '../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

const AllOccupancyGraph = () => {
    const [graphData, setGraphData] = useState([]);
    const globalProperty = useSelector(selectGlobalProperty);
    const [summaryOne, setSummaryOne] = useState({
        Sold: 0,
        UnSold: 0,
        Active: 0,
        InActive: 0,
        TotalBeds: 0,
        PartialBeds: 0,
    });
    const [summaryTwo, setSummaryTwo] = useState({
        Alloted: 0,
        UnAlloted: 0,
        TotalSold: 0,
    });

    const monthLabels = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    useEffect(() => {
        if (!globalProperty) return;
        let timeoutId;

        // Call the update api after 10 sec
        timeoutId = setTimeout(() => {
            refreshData();
        }, 10000)

        fetchData();
        fetchSummaryOne();
        fetchSummaryTwo();
        return () => clearTimeout(timeoutId); // clear the timeout after data fetched.
    }, [globalProperty]);

    const fetchData = async () => {
        try {
            const today = new Date();

            // Start from current month
            const startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

            // End at last day of the month 11 months from now
            const end = new Date(today.getFullYear(), today.getMonth() + 12, 0);
            const endDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
            const response = await axiosInstance.post('BedCalendar/GenerateBedCatCal', {
                BookDtFrom: startDate,
                CheckoutDt: endDate,
                PropId: globalProperty,
                Flag: 'Summary',
            });

            if (response.data.Status === 0 && Array.isArray(response.data.Data?.BedCatCalList)) {
                const rawData = response.data.Data.BedCatCalList;

                const monthMap = {};
                const monthLabels = [];

                // Create 12-month labels from current month
                for (let i = 0; i < 12; i++) {
                    const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
                    const label = date.toLocaleString('default', { month: 'short' }) + '-' + String(date.getFullYear()).slice(-2); // e.g., "Oct-25"
                    monthLabels.push(label);

                    monthMap[label] = {
                        month: label,
                        Booked: 0,
                        Occupied: 0,
                        BedToSale: 0,
                        BalBed: 0,
                    };
                }

                rawData.forEach(item => {
                    // Convert item.MonthNm + item.YearNm to label format "Jan-25"
                    const itemDate = new Date(`${item.MonthNm} 1, ${item.YearNm}`);
                    const label = itemDate.toLocaleString('default', { month: 'short' }) + '-' + String(itemDate.getFullYear()).slice(-2);

                    if (monthMap[label]) {
                        monthMap[label].Booked += Number(item.BookingBeds || 0);
                        monthMap[label].Occupied += Number(item.OccBeds || 0);
                        monthMap[label].BedToSale += Number(item.BedToSale || 0);
                        monthMap[label].BalBed += Number(item.BalBed || 0);
                    }
                });

                const formattedData = monthLabels.map(month => {
                    const booked = monthMap[month]?.Booked || 0;
                    const occupied = monthMap[month]?.Occupied || 0;
                    const balBed = monthMap[month]?.BalBed || 0;
                    const partialBeds = monthMap[month]?.PartialBeds || 0;

                    return {
                        month,
                        Booked: booked,
                        Occupied: occupied,  // Or change to: booked + occupied
                        Available: balBed,
                    };
                });
                setGraphData(formattedData);
            } else {
                toast.info('No response for this period.');
            }
        } catch (error) {
            toast.error('Error fetching data');
        }
    };

    const refreshData = async () => {
        try {
            const payload = {
                PropId: globalProperty,
                Flag: ""
            };

            await Promise.all([
                axiosInstance.post('BedCalendar/InsertBedCat', payload),
                axiosInstance.post('BedCalendar/InsertBedCatCal', payload),
                axiosInstance.post('BedCalendar/UpdateBedCat', payload),
                axiosInstance.post('BedCalendar/UpdateBedCatCal', payload),
                axiosInstance.post('BedCalendar/UpdateBedCatCal_Data', payload),
            ]);
            await fetchData();
        } catch (error) {
            toast.error("Error while refreshing the data." + error.message);
        }
    };

    const fetchSummaryOne = async () => {
        try {
            const today = new Date();
            const formattedDate = today.toISOString().split('T')[0];

            const response = await axiosInstance.post('BedCalendar/GenerateBedCatCal', {
                BookDtFrom: formattedDate,
                CheckoutDt: formattedDate,
                PropId: globalProperty,
                Flag: 'Summary1',
            });

            if (response.data.Status === 0 && Array.isArray(response.data.Data?.BedCatCalList)) {
                const bedData = response.data.Data.BedCatCalList;

                // Combine all objects into one total summary
                const summary = bedData.reduce((acc, item) => {
                    acc.Sold += Number(item.Sold || 0);
                    acc.UnSold += Number(item.UnSold || 0);
                    acc.PartialBeds += Number(item.PartialBeds || 0);
                    acc.Active += Number(item.Active || 0);
                    acc.InActive += Number(item.InActive || 0);
                    acc.TotalBeds += Number(item.TotalBeds || 0);
                    return acc;
                }, {
                    Sold: 0,
                    UnSold: 0,
                    PartialBeds: 0,
                    Active: 0,
                    InActive: 0,
                    TotalBeds: 0
                });
                setSummaryOne(summary);
            }
        } catch (error) {
            toast.error('Error fetching summary data.');
        }
    };

    const fetchSummaryTwo = async () => {
        try {
            const today = new Date();
            const formattedDate = today.toISOString().split('T')[0];

            const response = await axiosInstance.post('BedCalendar/GenerateBedCatCal', {
                BookDtFrom: formattedDate,
                CheckoutDt: formattedDate,
                PropId: globalProperty,
                Flag: 'Summary2',
            });

            if (response.data.Status === 0 && Array.isArray(response.data.Data?.BedCatCalList)) {
                const sumTwoData = response.data.Data.BedCatCalList;

                const summaryTwo = sumTwoData.reduce((acc, item) => {
                    acc.Alloted += Number(item.Alloted || 0);
                    acc.UnAlloted += Number(item.UnAlloted || 0);
                    acc.TotalSold += Number(item.TotalSold || 0);
                    return acc;
                }, {
                    Alloted: 0,
                    UnAlloted: 0,
                    TotalSold: 0,
                });
                setSummaryTwo(summaryTwo);
            }
        } catch (error) {
            toast.error("Error Fetching Data.");
        }
    };

    return (
        <Box sx={{ width: '100%', p: 2, overflowX: 'hidden' }}>
            <ToastContainer />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#635bff', userSelect: 'none', display: { xs: 'block', sm: 'none' }, }}>
                Overall Bed Status Count
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#635bff', userSelect: 'none', display: { xs: 'none', sm: 'flex' }, }}>
                    {/* Monthly Bed Status – {new Date().getFullYear()} */}
                    Overall Bed Status Count
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' }, // column on small, row on medium+
                        alignItems: 'flex-start',
                    }}
                >
                    {/* Top Section */}
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Tooltip title="Sold bed" arrow>
                            <Chip color="success" label={`Sold: ${summaryOne.Sold}`} />
                        </Tooltip>
                        <Tooltip title="Partial Beds" arrow>
                            <Chip sx={{ bgcolor: '#FF9800', color: '#fff', }}
                                label={`Partial Beds: ${summaryOne.PartialBeds}`}
                            />
                        </Tooltip>
                        <Tooltip title="Balanced Bed" arrow>
                            <Chip color="primary" label={`Unsold: ${summaryOne.UnSold}`} />
                        </Tooltip>
                        <Tooltip title="Total Active Bed" arrow>
                            <Chip color="warning" label={`Active: ${summaryOne.Active}`} />
                        </Tooltip>
                        <Tooltip title="Total inactive bed" arrow>
                            <Chip color="secondary" label={`Inactive: ${summaryOne.InActive}`} />
                        </Tooltip>
                        <Tooltip title="Total Beds" arrow>
                            <Chip color="info" label={`Total Beds: ${summaryOne.TotalBeds}`} />
                        </Tooltip>
                    </Stack>

                    {/* Sold Breakdown Section */}
                    <Box sx={{ mt: { xs: 0, md: 0 } }}>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: 'gray', ml: 1 }}
                        >
                            Sold Breakdown
                        </Typography>
                        <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
                            <Tooltip title="Allocated bed" arrow>
                                <Chip color="success" label={`Allocated: ${summaryTwo.Alloted}`} />
                            </Tooltip>
                            <Tooltip title="Unalloacted bed" arrow>
                                <Chip color="error" label={`Unalloacted: ${summaryTwo.UnAlloted}`} />
                            </Tooltip>
                            <Tooltip title="Total Sold" arrow>
                                <Chip
                                    sx={{ bgcolor: '#635bff', color: '#ffffff' }}
                                    label={`Total Sold: ${summaryTwo.TotalSold}`}
                                />
                            </Tooltip>
                        </Stack>
                    </Box>
                </Box>
            </Box>

            <ResponsiveContainer width="100%" height={400}>
                <BarChart
                    data={graphData}
                    barSize={40}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                        dataKey="month"
                        tick={{ fill: '#616161', fontSize: 12 }}
                        axisLine={{ stroke: '#9e9e9e' }}
                        tickLine={{ stroke: '#9e9e9e' }}
                    />
                    <YAxis
                        label={{
                            value: 'Total Beds',
                            angle: -90,
                            position: 'insideLeft',
                            fill: '#071542ff',
                            fontSize: 'bold',
                        }}
                        tick={{ fill: '#616161', fontSize: 12 }}
                        axisLine={{ stroke: '#9e9e9e' }}
                        tickLine={{ stroke: '#9e9e9e' }}
                    />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="Booked" stackId="a" fill="#635bff" name="Booked" label={{ fill: '#fff', fontSize: 12 }} />
                    <Bar dataKey="Occupied" stackId="a" fill="rgb(0, 153, 102)" name="Occupied" label={{ fill: '#fff', fontSize: 12 }} />
                    <Bar dataKey="Available" fill="rgb(255, 102, 102)" name="Available" label={{ fill: '#fff', fontSize: 12 }} />
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default AllOccupancyGraph;